import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { AppPrimaryButtonOutlined } from 'shared/components/AppComponents';
import AddIcon from '@mui/icons-material/Add';
import { PoliciesService } from 'services';
import CloseIcon from '@mui/icons-material/Close';
import { Utils } from 'shared/helpers';
import { useParams } from 'react-router-dom';
import { BROADCAST_MESSAGES } from 'shared/Constants';

export interface IComplianceTemplate {
  _id: string;
  name: string;
  issuer: string;
  framework_type: string;
  description: string;
  region: string;
  isActive: boolean;
  created_at: string;
  updated_at: string;
}

interface IComplianceProps {
  addedFrameworks: IComplianceTemplate[];
}

const Compliance: React.FC<IComplianceProps> = (props): ReactElement => {
  const { addedFrameworks } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<IComplianceTemplate[]>([]);
  const { policyId } = useParams();
  const [showAddFramework, setShowAddFramework] = useState(false);
  const addFrameworkRef = useRef<any>(null);

  const sortFrameworks = (
    frameworks: IComplianceTemplate[]
  ): IComplianceTemplate[] =>
    [...frameworks].sort((a, b) => a.name.localeCompare(b.name));

  const availableTemplates = useMemo(
    () =>
      templates.filter((t) => !addedFrameworks.some((a) => a._id === t._id)),
    [addedFrameworks, templates]
  );

  const sortedAddedFrameworks = useMemo(
    () => sortFrameworks(addedFrameworks),
    [addedFrameworks]
  );

  const sortedAvailableTemplates = useMemo(
    () => sortFrameworks(availableTemplates),
    [availableTemplates]
  );

  const sortedTemplates = useMemo(() => sortFrameworks(templates), [templates]);

  // API Calls
  const loadComplainceTemplates = (): void => {
    setIsLoading(true);
    PoliciesService.policyTemplates()
      .then((response) => {
        const { data } = response.data;
        setTemplates(data.frameworks);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const addOrRemoveFramework = (
    id: string,
    action: 'link' | 'unlink'
  ): void => {
    setIsLoading(true);
    PoliciesService.addFrameworkToPolicy(`${policyId}`, {
      frameworkId: id,
      action,
    })
      .then(() => {
        window.postMessage(BROADCAST_MESSAGES.RELOAD_POLICY_DETAILS);
        setIsLoading(false);
        setShowAddFramework(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        setIsLoading(false);
      });
  };

  // Actions
  const actionOnAddTemplate = (template: IComplianceTemplate): void => {
    addOrRemoveFramework(template._id, 'link');
  };
  const actionOnRemoveTemplate = (template: IComplianceTemplate): void => {
    addOrRemoveFramework(template._id, 'unlink');
  };

  useEffect(() => {
    if (showAddFramework && addFrameworkRef.current) {
      addFrameworkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showAddFramework]);

  // Hooks
  useEffect(() => {
    loadComplainceTemplates();
  }, []);

  console.log(addedFrameworks, 'addedFrameworksaddedFrameworks');

  // UI Elements
  return (
    <Box className="app-plain-card">
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box className="title">
          <Stack direction="row" alignItems="center" spacing={2}>
            <img height={20} src="/images/icons/IconShield.svg" />
            <Typography variant="h4">Compliance Frameworks</Typography>
          </Stack>
          <Typography variant="body1" className="text-gray">
            Select the compliance frameworks this policy addresses
          </Typography>
        </Box>
        <AppPrimaryButtonOutlined
          onClick={() => {
            setShowAddFramework(true);
          }}
          startIcon={<AddIcon />}
          className="btn-small mr-5"
          title="Add Framework"
        />
      </Stack>
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Box className="content px-5">
          {sortedAddedFrameworks.length === 0 && (
            <Box className="empty-content">
              <Typography>No Frameworks added</Typography>
            </Box>
          )}
          {sortedAddedFrameworks.length > 0 && (
            <>
              <Typography className="f18 mb-5">Selected Frameworks</Typography>
              {sortedAddedFrameworks.map((template) => (
                <List key={template._id} className="app-box-border mt-4">
                  <ListItem>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ width: '100%' }}
                    >
                      <Stack>
                        <Stack
                          className="text-primary mb-2"
                          alignItems="center"
                          direction="row"
                          spacing={2}
                        >
                          <Typography className="f18 fw-6">
                            {template.name}
                          </Typography>
                          <Chip
                            color="primary"
                            size="small"
                            label={template.framework_type}
                          />
                          <Chip
                            sx={{ backgroundColor: '#fee2e2', color: 'red' }}
                            size="small"
                            label={template.region}
                          />
                        </Stack>
                        <Typography className="f14 fw-6">
                          {template.issuer}
                        </Typography>

                        <Typography variant="body1" className="text-gray  mb-2">
                          {template.description}
                        </Typography>
                      </Stack>
                      <IconButton
                        onClick={() => {
                          actionOnRemoveTemplate(template);
                        }}
                      >
                        <CloseIcon color="primary" />
                      </IconButton>
                    </Stack>
                  </ListItem>
                </List>
              ))}
            </>
          )}
          {showAddFramework && (
            <>
              <Typography className="f18 my-5" ref={addFrameworkRef}>
                Frameworks ({sortedAddedFrameworks.length}/
                {sortedTemplates.length} Selected)
              </Typography>
              <Box className="app-box-border">
                <Typography className="f18 mb-5">
                  Available Frameworks
                </Typography>
                {sortedAvailableTemplates.map((template) => (
                  <List key={template._id} className="app-box-border mt-4">
                    <ListItem>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ width: '100%' }}
                      >
                        <Stack>
                          <Stack
                            className="text-primary mb-2"
                            alignItems="center"
                            direction="row"
                            spacing={2}
                          >
                            <Typography className="f18 fw-6">
                              {template.name}
                            </Typography>
                            <Chip
                              color="primary"
                              size="small"
                              label={template.framework_type}
                            />
                            <Chip
                              sx={{ backgroundColor: '#fee2e2', color: 'red' }}
                              size="small"
                              label={template.region}
                            />
                          </Stack>
                          <Typography className="f14 fw-6">
                            {template.issuer}
                          </Typography>

                          <Typography
                            variant="body1"
                            className="text-gray  mb-2"
                          >
                            {template.description}
                          </Typography>
                        </Stack>
                        <IconButton
                          onClick={() => {
                            actionOnAddTemplate(template);
                          }}
                        >
                          <AddIcon color="primary" />
                        </IconButton>
                      </Stack>
                    </ListItem>
                  </List>
                ))}
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Compliance;
